import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { getUserList } from 'src/actions/user-management.action'
import { getProjectList } from 'src/actions/project-management.action'
import { getResourceAllocations } from 'src/actions/timesheet.action'
import { warningSnackBar } from 'src/actions/common.action'
import { getGlobalSettingReducer } from 'src/actions/global-setting.action'
import { getUserWorkHourAssignment, getWorkHourSetting } from 'src/actions/work-hour-settings.action'
import { TIMESHEET, IMPORT_TIMESHEET } from 'src/constants/route-paths'

import '../../projects/scss/task.scss'
import './scss/scheduling.scss'

import SchedulingToolbar          from './components/scheduling-toolbar'
import LegendBar                  from './components/legend-bar'
import StaffGrid                  from './components/staff-grid'
import ProjectGrid                from './components/project-grid'
import SelectionActionBar         from './components/selection-action-bar'
import StaffDrawer                from './components/staff-drawer'
import ConfirmAllocationsDialog   from './components/confirm-allocations-dialog'
import AssignStaffDialog          from './components/assign-staff-dialog'
import { useGridSelection }       from './use-grid-selection'
import { getAssignmentSetting, getAssignmentSettingId } from './work-hour-allocation'
import {
  addDays, buildDays, buildScheduleMap, dateKey, hasConflict,
  startOfWeek, VIEW_DAYS, HOURS_PER_DAY, availableDaysFromUserRecord, getStandardHours,
  exceptionDatesFromSetting,
} from './utils'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Number of staff auto-loaded into the grid initially, and per "Show more" click.
const STAFF_BATCH_SIZE = 10

/** Map a raw user API object to the scheduler roster shape. */
function mapUser(u) {
  const first = u.firstName || ''
  const last  = u.lastName  || ''
  return {
    id:           u.id,
    name:         `${first} ${last}`.trim(),
    role:         u.roles?.[0]?.name || '',
    hourlyRate:   Number(u.hourlyRate) || 0, // kept for future cost display
    initials:     `${first[0] || ''}${last[0] || ''}`.toUpperCase(),
    availableDays: availableDaysFromUserRecord(u.available_days), // Set<dayName> | null
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SchedulingPage() {
  const dispatch  = useDispatch()
  const navigate  = useNavigate()
  const globalSetting = useSelector((state) => state.globalSetting)
  const standardHours = useMemo(() => getStandardHours(globalSetting?.letterhead), [globalSetting])

  // View state
  const [layout,     setLayout]     = useState('staff')
  const [view,       setView]       = useState('week')
  const [rangeStart, setRangeStart] = useState(() => startOfWeek(new Date()))

  // Data
  const [staffList,   setStaffList]   = useState([])  // full roster for drawer
  const [scheduledIds, setScheduledIds] = useState([]) // IDs visible in grid
  const [visibleCount, setVisibleCount] = useState(0)  // how many of the alphabetical roster are auto-loaded
  const [scheduleMap,  setScheduleMap]  = useState(new Map())
  const [exceptionDates, setExceptionDates] = useState(new Map()) // Map<userId, Map<date, { name, type }>>
  const [projects,    setProjects]    = useState([])
  const [allocsLoading, setAllocsLoading] = useState(false)

  // UI
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Derived
  const days    = useMemo(() => buildDays(rangeStart, VIEW_DAYS[view]), [rangeStart, view])
  const dayKeys = useMemo(() => days.map(dateKey), [days])
  const rangeEnd = days[days.length - 1]

  const scheduledStaff = useMemo(
    () => staffList.filter((s) => scheduledIds.includes(s.id)),
    [staffList, scheduledIds],
  )

  const remainingStaffCount = Math.max(staffList.length - visibleCount, 0)
  const hasMoreStaff = remainingStaffCount > 0

  // Map<userId, Set<dayName>> — from each user's "Available Days" profile setting
  const availability = useMemo(() => {
    const map = new Map()
    staffList.forEach((s) => { if (s.availableDays) map.set(s.id, s.availableDays) })
    return map
  }, [staffList])

  const rowIds = layout === 'staff' ? scheduledIds : projects.map((p) => p.id)
  const selection = useGridSelection(rowIds, dayKeys)
  const { selected, clear } = selection

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  useEffect(() => {
    loadInitialData()
  }, [])

  const loadAllocations = useCallback(async (daysToLoad) => {
    if (!daysToLoad?.length) return
    setAllocsLoading(true)
    try {
      const result = await dispatch(
        getResourceAllocations({
          startTime: daysToLoad[0],
          endTime:   daysToLoad[daysToLoad.length - 1],
        }),
      ).catch(() => null)

      const raw = result?.data ?? (Array.isArray(result) ? result : [])
      setScheduleMap(buildScheduleMap(raw, standardHours))
    } catch (e) {
      console.error('[Scheduling] loadAllocations:', e)
    } finally {
      setAllocsLoading(false)
    }
  }, [dispatch, standardHours])

  // Reload allocations when the visible period, roster, or standard hours change
  useEffect(() => {
    if (scheduledIds.length === 0) return
    loadAllocations(days)
  }, [rangeStart, view, scheduledIds, standardHours]) // eslint-disable-line react-hooks/exhaustive-deps

  // Exception dates come from each user's assigned Work Hour Config — fetched only for
  // staff visible in the grid, and cached so paging in more staff doesn't refetch.
  const loadedExceptionIdsRef = useRef(new Set())

  const loadExceptionDates = useCallback(async (ids) => {
    const toFetch = ids.filter((id) => !loadedExceptionIdsRef.current.has(id))
    if (toFetch.length === 0) return
    toFetch.forEach((id) => loadedExceptionIdsRef.current.add(id))

    const entries = await Promise.all(toFetch.map(async (staffId) => {
      try {
        const assignmentResponse = await dispatch(getUserWorkHourAssignment(staffId))
        const settingId          = getAssignmentSettingId(assignmentResponse)
        const embeddedSetting    = getAssignmentSetting(assignmentResponse)
        // Fall back to a full fetch by ID if the embedded setting lacks exception data.
        const hasExceptionData   = embeddedSetting?.date_exceptions || embeddedSetting?.dateExceptions
        const settingResponse    = hasExceptionData ? embeddedSetting
          : (settingId ? await dispatch(getWorkHourSetting(settingId)) : null)
        const setting            = settingResponse?.data || settingResponse
        return [staffId, exceptionDatesFromSetting(setting)]
      } catch (e) {
        console.error('[Scheduling] loadExceptionDates:', staffId, e)
        return [staffId, new Map()]
      }
    }))

    setExceptionDates((prev) => {
      const next = new Map(prev)
      entries.forEach(([id, dates]) => { if (dates.size) next.set(id, dates) })
      return next
    })
  }, [dispatch])

  useEffect(() => {
    if (scheduledIds.length) loadExceptionDates(scheduledIds)
  }, [scheduledIds, loadExceptionDates])

  async function loadInitialData() {
    try {
      // Account work hours (UTC) — needed by ConfirmAllocationsDialog for allocation start/end times
      dispatch(getGlobalSettingReducer('LETTERHEAD')).catch((e) => console.error('[Scheduling] loadGlobalSetting:', e))

      // Staff: active users, sorted alphabetically, first batch only (rest via "Show more")
      const users = await dispatch(getUserList({ status: 'ACTIVE' })).catch(() => [])
      if (Array.isArray(users) && users.length > 0) {
        const roster = users
          .filter((u) => !u.isDeleted && u.status !== 'INACTIVE')
          .map(mapUser)
          .sort((a, b) => a.name.localeCompare(b.name))
        const initialBatch = roster.slice(0, STAFF_BATCH_SIZE)
        setStaffList(roster)
        setScheduledIds(initialBatch.map((s) => s.id))
        setVisibleCount(initialBatch.length)
      }

      // Projects: IN_PROGRESS only
      const projList = await dispatch(getProjectList(null, 'PROJECT', null)).catch(() => [])
      if (Array.isArray(projList)) {
        setProjects(
          projList
            .filter((p) => p.status === 'IN_PROGRESS')
            .map((p) => ({
              id:   p.id   || p.value,
              name: p.name || p.key,
              type: p.projectType || p.type || 'PROJECT',
            })),
        )
      }
    } catch (e) {
      console.error('[Scheduling] loadInitialData:', e)
    }
  }

  // -------------------------------------------------------------------------
  // Interaction handlers
  // -------------------------------------------------------------------------

  function shiftPeriod(direction) {
    setRangeStart((prev) => addDays(prev, direction * VIEW_DAYS[view]))
  }

  function handleLayoutChange(next) {
    clear()
    setLayout(next)
  }

  function handleAddStaff(id) {
    setScheduledIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }

  function handleRemoveStaff(id) {
    setScheduledIds((prev) => prev.filter((x) => x !== id))
  }

  // Reveal the next alphabetical batch of staff into the grid (paged default view).
  function handleShowMore() {
    const nextCount = Math.min(visibleCount + STAFF_BATCH_SIZE, staffList.length)
    const newIds    = staffList.slice(visibleCount, nextCount).map((s) => s.id)
    setScheduledIds((prev) => [...prev, ...newIds.filter((id) => !prev.includes(id))])
    setVisibleCount(nextCount)
  }

  function handleConfirmed() {
    clear()
    setDialogOpen(false)
    loadAllocations(days) // refresh grid after new allocations
  }

  function handleAction() {
    if (!active) {
      dispatch(warningSnackBar('Please select at least a day to allocate'))
      return
    }
    setDialogOpen(true)
  }

  // -------------------------------------------------------------------------
  // Summary stats for the action bar
  // -------------------------------------------------------------------------

  const active = selected.size > 0

  const summary = useMemo(() => {
    const rows       = new Set()
    const dys        = new Set()
    const byStaff    = new Map()
    let conflicts    = 0
    let cost         = 0
    const rateById   = new Map(scheduledStaff.map((s) => [s.id, s.hourlyRate]))

    selected.forEach((k) => {
      const [rowId, day] = k.split('|')
      rows.add(rowId)
      dys.add(day)
      if (layout === 'staff') {
        const arr = byStaff.get(rowId) ?? []
        arr.push(day)
        byStaff.set(rowId, arr)
        if (hasConflict(scheduleMap, rowId, day, availability, standardHours)) conflicts++
        cost += (rateById.get(rowId) ?? 0) * standardHours
      }
    })

    return {
      rowCount: rows.size,
      dayCount: dys.size,
      cells:    selected.size,
      hours:    selected.size * standardHours,
      cost,       // calculated, display hidden in v1
      conflicts,
      byStaff,
    }
  }, [selected, layout, scheduleMap, scheduledStaff, availability, standardHours])

  const stats = layout === 'staff'
    ? [
        { value: `${summary.rowCount}`, label: summary.rowCount === 1 ? 'Person'  : 'People'  },
        { value: `${summary.dayCount}`, label: summary.dayCount === 1 ? 'Day'     : 'Days'    },
        { value: `${summary.cells}`,    label: 'Allocations'                                  },
        { value: `${summary.hours}h`,   label: 'Total hours'                                  },
        // Cost stat hidden in v1 — uncomment to show:
        // { value: `$${summary.cost.toLocaleString()}`, label: 'Est. cost' },
      ]
    : [
        { value: `${summary.rowCount}`, label: summary.rowCount === 1 ? 'Project' : 'Projects' },
        { value: `${summary.dayCount}`, label: summary.dayCount === 1 ? 'Day'     : 'Days'     },
        { value: `${summary.cells}`,    label: 'Slots'                                         },
      ]

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fff' }}>

      <SchedulingToolbar
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        view={view}
        onViewChange={setView}
        layout={layout}
        onLayoutChange={handleLayoutChange}
        onPrev={() => shiftPeriod(-1)}
        onNext={() => shiftPeriod(1)}
        onToday={() => setRangeStart(startOfWeek(new Date()))}
        onAddStaff={() => setDrawerOpen(true)}
        scheduledCount={scheduledStaff.length}
        totalStaffCount={staffList.length}
        onImportTimesheet={() => navigate(`/${TIMESHEET}/${IMPORT_TIMESHEET}`)}
      />

      <LegendBar layout={layout} loading={allocsLoading} />

      {layout === 'staff' ? (
        <StaffGrid
          staff={scheduledStaff}
          days={days}
          scheduleMap={scheduleMap}
          availability={availability}
          exceptionDates={exceptionDates}
          standardHours={standardHours}
          selection={selection}
          onRemoveStaff={handleRemoveStaff}
          onAddStaff={() => setDrawerOpen(true)}
          hasMore={hasMoreStaff}
          remainingCount={remainingStaffCount}
          onShowMore={handleShowMore}
        />
      ) : (
        <ProjectGrid
          projects={projects}
          days={days}
          roster={staffList}
          scheduleMap={scheduleMap}
          selection={selection}
        />
      )}

      <SelectionActionBar
        active={active}
        stats={stats}
        conflicts={summary.conflicts}
        idleHint={
          layout === 'staff'
            ? 'Click or drag across the grid to select people and days, then review.'
            : 'Select project days, then assign staff to them in bulk.'
        }
        actionLabel={layout === 'staff' ? 'Review & Assign' : 'Assign Staff'}
        onClear={clear}
        onAction={handleAction}
      />

      {/* Staff drawer — slide in from left, shows full roster with search/filter */}
      <StaffDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        staffList={staffList}
        scheduledIds={scheduledIds}
        onAdd={handleAddStaff}
      />

      {/* Dialogs — by-staff: confirm + project/task; by-project: pick staff */}
      {layout === 'staff' ? (
        <ConfirmAllocationsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          staff={scheduledStaff}
          selectionByStaff={summary.byStaff}
          scheduleMap={scheduleMap}
          availability={availability}
          projects={projects}
          onConfirm={handleConfirmed}
        />
      ) : (
        <AssignStaffDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          selection={selected}
          projects={projects}
          roster={staffList}
          scheduleMap={scheduleMap}
          onConfirm={handleConfirmed}
        />
      )}

    </div>
  )
}
